import { useEffect, useState } from 'react'
import { useForm, Controller, useFieldArray } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, MenuItem,
  Alert, CircularProgress, Tabs, Tab, IconButton, Box, Checkbox, FormControlLabel,
} from '@mui/material'
import { Add, DeleteOutlined } from '@mui/icons-material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createClient, updateClient, getClient, listSalesAgents, type ClientListItem, type SaveClientPayload } from '../../api/clienti'
import { listPriceLists } from '../../api/preturi'
import { errorMessage } from '../../api/client'

const addressSchema = z.object({
  id: z.string().nullable(),
  type: z.enum(['Livrare', 'Facturare']),
  line: z.string().min(1, 'Câmp obligatoriu'),
  city: z.string().optional(), county: z.string().optional(), postalCode: z.string().optional(), country: z.string().optional(),
  isDefault: z.boolean(),
})
const contactSchema = z.object({
  id: z.string().nullable(),
  name: z.string().min(1, 'Câmp obligatoriu'),
  jobTitle: z.string().optional(), phone: z.string().optional(), email: z.string().email('Email invalid').optional().or(z.literal('')),
})
const schema = z.object({
  code: z.string().min(1, 'Câmp obligatoriu').max(32),
  name: z.string().min(1, 'Câmp obligatoriu').max(256),
  cui: z.string().optional(), regCom: z.string().optional(),
  headOfficeAddress: z.string().optional(), headOfficeCity: z.string().optional(),
  headOfficeCounty: z.string().optional(), headOfficeCountry: z.string().optional(),
  marketSegment: z.union([z.enum(['Lapte', 'Delicatese', 'Blistere', 'Altele']), z.literal('')]).optional(),
  notes: z.string().optional(),
  paymentTermDays: z.string().optional(), preferredCurrency: z.string().optional(),
  standardDiscountPercent: z.string().optional(), skontoPercent: z.string().optional(),
  salesAgentUserId: z.string().optional(),
  priceListId: z.string().optional(), creditLimit: z.string().optional(),
  addresses: z.array(addressSchema),
  contacts: z.array(contactSchema),
})
type FormValues = z.infer<typeof schema>

const EMPTY: FormValues = {
  code: '', name: '', cui: '', regCom: '', headOfficeAddress: '', headOfficeCity: '', headOfficeCounty: '', headOfficeCountry: '',
  marketSegment: '', notes: '', paymentTermDays: '', preferredCurrency: '', standardDiscountPercent: '',
  skontoPercent: '', salesAgentUserId: '', priceListId: '', creditLimit: '', addresses: [], contacts: [],
}

function numOrNull(v: string | undefined): number | null {
  if (v === undefined || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export default function ClientFormDialog({ client, onClose }: { client: ClientListItem | null | 'new'; onClose: () => void }) {
  const open = client !== null
  const editing = client !== null && client !== 'new'
  const qc = useQueryClient()
  const [tab, setTab] = useState(0)

  const { data: agents } = useQuery({ queryKey: ['comercial', 'agenti-vanzari'], queryFn: listSalesAgents, enabled: open })
  const { data: priceLists } = useQuery({ queryKey: ['comercial', 'preturi', 'liste'], queryFn: listPriceLists, enabled: open })
  const { data: detail } = useQuery({
    queryKey: ['comercial', 'client', editing ? (client as ClientListItem).id : null],
    queryFn: () => getClient((client as ClientListItem).id),
    enabled: editing,
  })

  const { control, handleSubmit, reset, formState: { errors } } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY })
  const addresses = useFieldArray({ control, name: 'addresses' })
  const contacts = useFieldArray({ control, name: 'contacts' })

  useEffect(() => {
    if (editing && detail) {
      reset({
        code: detail.code, name: detail.name, cui: detail.cui ?? '', regCom: detail.regCom ?? '',
        headOfficeAddress: detail.headOfficeAddress ?? '', headOfficeCity: detail.headOfficeCity ?? '',
        headOfficeCounty: detail.headOfficeCounty ?? '', headOfficeCountry: detail.headOfficeCountry ?? '',
        marketSegment: detail.marketSegment ?? '', notes: detail.notes ?? '',
        paymentTermDays: detail.paymentTermDays != null ? String(detail.paymentTermDays) : '', preferredCurrency: detail.preferredCurrency ?? '',
        standardDiscountPercent: detail.standardDiscountPercent != null ? String(detail.standardDiscountPercent) : '',
        skontoPercent: detail.skontoPercent != null ? String(detail.skontoPercent) : '',
        salesAgentUserId: detail.salesAgentUserId ?? '', priceListId: detail.priceListId ?? '',
        creditLimit: detail.creditLimit != null ? String(detail.creditLimit) : '',
        addresses: detail.addresses.map((a) => ({ ...a, city: a.city ?? '', county: a.county ?? '', postalCode: a.postalCode ?? '', country: a.country ?? '' })),
        contacts: detail.contacts.map((c) => ({ ...c, jobTitle: c.jobTitle ?? '', phone: c.phone ?? '', email: c.email ?? '' })),
      })
    } else if (client === 'new') {
      reset(EMPTY)
      setTab(0)
    }
  }, [client, editing, detail, reset])

  const mutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const paymentTermDays = numOrNull(values.paymentTermDays)
      const payload: SaveClientPayload = {
        code: values.code, name: values.name,
        cui: values.cui || null, regCom: values.regCom || null,
        headOfficeAddress: values.headOfficeAddress || null, headOfficeCity: values.headOfficeCity || null,
        headOfficeCounty: values.headOfficeCounty || null, headOfficeCountry: values.headOfficeCountry || null,
        marketSegment: values.marketSegment || null, notes: values.notes || null,
        paymentTermDays: paymentTermDays != null ? Math.trunc(paymentTermDays) : null, preferredCurrency: values.preferredCurrency || null,
        standardDiscountPercent: numOrNull(values.standardDiscountPercent), skontoPercent: numOrNull(values.skontoPercent),
        salesAgentUserId: values.salesAgentUserId || null, priceListId: values.priceListId || null,
        creditLimit: numOrNull(values.creditLimit),
        addresses: values.addresses.map((a) => ({ ...a, city: a.city || null, county: a.county || null, postalCode: a.postalCode || null, country: a.country || null })),
        contacts: values.contacts.map((c) => ({ ...c, jobTitle: c.jobTitle || null, phone: c.phone || null, email: c.email || null })),
      }
      if (editing) return updateClient((client as ClientListItem).id, payload)
      return createClient(payload)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['comercial', 'clienti'] })
      onClose()
    },
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit((v) => mutation.mutate(v))}>
        <DialogTitle>{editing ? 'Editare client' : 'Client nou'}</DialogTitle>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 3 }}>
          <Tab label="General" />
          <Tab label={`Adrese (${addresses.fields.length})`} />
          <Tab label={`Contacte (${contacts.fields.length})`} />
        </Tabs>
        <DialogContent>
          {tab === 0 && (
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Stack direction="row" spacing={2}>
                <Controller name="code" control={control} render={({ field }) => (
                  <TextField {...field} label="Cod" fullWidth error={!!errors.code} helperText={errors.code?.message} />
                )} />
                <Controller name="name" control={control} render={({ field }) => (
                  <TextField {...field} label="Denumire" fullWidth error={!!errors.name} helperText={errors.name?.message} />
                )} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Controller name="cui" control={control} render={({ field }) => (<TextField {...field} label="CUI/CIF" fullWidth />)} />
                <Controller name="regCom" control={control} render={({ field }) => (<TextField {...field} label="Nr. Reg. Comerț" fullWidth />)} />
                <Controller name="marketSegment" control={control} render={({ field }) => (
                  <TextField {...field} select label="Segment de piață" fullWidth>
                    <MenuItem value="">—</MenuItem>
                    <MenuItem value="Lapte">Lapte</MenuItem>
                    <MenuItem value="Delicatese">Delicatese</MenuItem>
                    <MenuItem value="Blistere">Blistere</MenuItem>
                    <MenuItem value="Altele">Altele</MenuItem>
                  </TextField>
                )} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Controller name="headOfficeAddress" control={control} render={({ field }) => (<TextField {...field} label="Adresă sediu social" fullWidth />)} />
                <Controller name="headOfficeCity" control={control} render={({ field }) => (<TextField {...field} label="Oraș" fullWidth />)} />
                <Controller name="headOfficeCountry" control={control} render={({ field }) => (<TextField {...field} label="Țară" fullWidth />)} />
              </Stack>

              <Stack direction="row" spacing={2}>
                <Controller name="paymentTermDays" control={control} render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} type="number" label="Termen de plată (zile)" fullWidth />
                )} />
                <Controller name="preferredCurrency" control={control} render={({ field }) => (<TextField {...field} label="Monedă preferată" fullWidth />)} />
                <Controller name="standardDiscountPercent" control={control} render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} type="number" label="Discount standard (%)" fullWidth />
                )} />
                <Controller name="skontoPercent" control={control} render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} type="number" label="Skonto (%)" fullWidth />
                )} />
              </Stack>
              <Stack direction="row" spacing={2}>
                <Controller name="salesAgentUserId" control={control} render={({ field }) => (
                  <TextField {...field} select label="Agent de vânzări" fullWidth>
                    <MenuItem value="">—</MenuItem>
                    {agents?.map((a) => <MenuItem key={a.id} value={a.id}>{a.fullName}</MenuItem>)}
                  </TextField>
                )} />
                <Controller name="creditLimit" control={control} render={({ field }) => (
                  <TextField {...field} value={field.value ?? ''} type="number" label="Limită de credit" fullWidth />
                )} />
                <Controller name="priceListId" control={control} render={({ field }) => (
                  <TextField {...field} select label="Listă de preț" fullWidth>
                    <MenuItem value="">—</MenuItem>
                    {priceLists?.map((p) => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                  </TextField>
                )} />
              </Stack>
              <Controller name="notes" control={control} render={({ field }) => (
                <TextField {...field} label="Note" fullWidth multiline minRows={2} />
              )} />
            </Stack>
          )}

          {tab === 1 && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              {addresses.fields.map((f, i) => (
                <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                  <Controller name={`addresses.${i}.type`} control={control} render={({ field }) => (
                    <TextField {...field} select label="Tip" sx={{ width: 140 }}
                      error={!!errors.addresses?.[i]?.type} helperText={errors.addresses?.[i]?.type?.message}>
                      <MenuItem value="Livrare">Livrare</MenuItem>
                      <MenuItem value="Facturare">Facturare</MenuItem>
                    </TextField>
                  )} />
                  <Controller name={`addresses.${i}.line`} control={control} render={({ field }) => (
                    <TextField {...field} label="Adresă" fullWidth
                      error={!!errors.addresses?.[i]?.line} helperText={errors.addresses?.[i]?.line?.message} />
                  )} />
                  <Controller name={`addresses.${i}.city`} control={control} render={({ field }) => (<TextField {...field} label="Oraș" sx={{ width: 160 }} />)} />
                  <Controller name={`addresses.${i}.isDefault`} control={control} render={({ field }) => (
                    <FormControlLabel sx={{ whiteSpace: 'nowrap' }} control={<Checkbox checked={field.value} onChange={field.onChange} />} label="Implicit" />
                  )} />
                  <IconButton onClick={() => addresses.remove(i)}><DeleteOutlined fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<Add />}
                  onClick={() => addresses.append({ id: null, type: 'Livrare', line: '', city: '', county: '', postalCode: '', country: '', isDefault: false })}>
                  Adaugă adresă
                </Button>
              </Box>
            </Stack>
          )}

          {tab === 2 && (
            <Stack spacing={1.5} sx={{ mt: 0.5 }}>
              {contacts.fields.map((f, i) => (
                <Stack key={f.id} direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                  <Controller name={`contacts.${i}.name`} control={control} render={({ field }) => (
                    <TextField {...field} label="Nume" fullWidth error={!!errors.contacts?.[i]?.name} helperText={errors.contacts?.[i]?.name?.message} />
                  )} />
                  <Controller name={`contacts.${i}.jobTitle`} control={control} render={({ field }) => (<TextField {...field} label="Funcție" fullWidth />)} />
                  <Controller name={`contacts.${i}.phone`} control={control} render={({ field }) => (<TextField {...field} label="Telefon" fullWidth />)} />
                  <Controller name={`contacts.${i}.email`} control={control} render={({ field }) => (
                    <TextField {...field} label="Email" fullWidth error={!!errors.contacts?.[i]?.email} helperText={errors.contacts?.[i]?.email?.message} />
                  )} />
                  <IconButton onClick={() => contacts.remove(i)}><DeleteOutlined fontSize="small" /></IconButton>
                </Stack>
              ))}
              <Box>
                <Button size="small" startIcon={<Add />} onClick={() => contacts.append({ id: null, name: '', jobTitle: '', phone: '', email: '' })}>
                  Adaugă contact
                </Button>
              </Box>
            </Stack>
          )}

          {Object.keys(errors).length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>Există câmpuri obligatorii necompletate — verificați câmpurile marcate cu roșu mai sus.</Alert>
          )}
          {mutation.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(mutation.error)}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={onClose}>Renunță</Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : undefined}>
            Salvează
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  )
}
